// HTTP 클라이언트 — same-origin /api 기본. FastAPI 422 detail 파싱.
// web_legacy의 fetchJson 패턴을 재사용하되 TS 친화적으로 재작성.

export class ApiHttpError extends Error {
  status?: number;
  detail?: unknown;

  constructor(message: string, options?: { status?: number; detail?: unknown }) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = options?.status;
    this.detail = options?.detail;
  }
}

function looksLikeHtml(text: string): boolean {
  const sample = text.trimStart().slice(0, 64).toLowerCase();
  return sample.startsWith('<!doctype') || sample.startsWith('<html') || sample.startsWith('<');
}

function buildNonJsonMessage(url: string): string {
  return `API가 JSON이 아닌 응답을 반환했습니다. same-origin API 또는 backend URL 설정을 확인하세요: ${url}`;
}

async function readText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return '';
  }
}

function extractErrorMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const detail = (data as Record<string, unknown>).detail;
  // FastAPI string detail
  if (typeof detail === 'string') return detail;
  // FastAPI 422 validation error: detail = [{ msg, loc, type }, ...]
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item) => {
        if (typeof item !== 'object' || item === null) return null;
        const d = item as Record<string, unknown>;
        const loc = Array.isArray(d.loc) ? (d.loc as string[]).slice(1).join('.') : '';
        const msg = typeof d.msg === 'string' ? d.msg : '';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join(', ');
  }
  return null;
}

const BASE = '/api';

// 세션 만료(401) 시 로그인 화면으로 보낸다. /auth/* 는 제외 — /auth/me 401 은 Layout 이 처리하고,
// 로그인 화면이 부르는 엔드포인트에서 리다이렉트하면 루프가 생긴다.
let redirectingToLogin = false;

function shouldRedirectToLogin(path: string): boolean {
  if (path.startsWith('/auth/')) return false;
  return window.location.pathname !== '/login';
}

function redirectToLogin(): void {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  const ret = encodeURIComponent(window.location.pathname + window.location.search);
  // 라우터 밖이라 전체 페이지 이동 — 만료된 세션의 화면 상태도 함께 정리된다.
  window.location.assign(`/login?return_to=${ret}`);
}

// 쿼리 스트링 직렬화 — undefined/null 키 자동 제거.
export function buildQuery(params: { [key: string]: unknown } | undefined): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [key, raw] of Object.entries(params)) {
    if (raw === undefined || raw === null || raw === '') continue;
    if (typeof raw === 'boolean') {
      sp.set(key, raw ? 'true' : 'false');
    } else {
      sp.set(key, String(raw));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// 쿼리는 어떤 객체든 받아들이도록 — 도메인 query 인터페이스를 직접 통과 가능.
type QueryRecord = { [key: string]: unknown };

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: QueryRecord;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = `${BASE}${path}${buildQuery(opts.query)}`;

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: 'same-origin',
    signal: opts.signal,
    headers: {
      Accept: 'application/json',
      ...opts.headers,
    },
  };

  if (opts.body !== undefined) {
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    throw new ApiHttpError(`네트워크 오류: ${url}`);
  }

  if (response.status === 401 && shouldRedirectToLogin(path)) {
    redirectToLogin();
    // 이동하는 동안 각 화면이 "인증이 필요합니다" 오류를 띄우지 않도록 결과를 보류한다.
    return new Promise<T>(() => {});
  }

  const contentType = response.headers.get('content-type') ?? '';
  const rawText = await readText(response);

  // 본문이 비어 있는 204 류는 빈 객체로 반환
  if (response.status === 204 || rawText === '') {
    if (!response.ok) {
      throw new ApiHttpError(`요청 처리 실패: ${response.status}`, { status: response.status });
    }
    return {} as T;
  }

  let parsed: unknown = null;
  if (contentType.includes('application/json')) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      // fallthrough
    }
  }

  if (!response.ok) {
    let message = `요청 처리 실패: ${response.status}`;
    if (parsed) {
      const extracted = extractErrorMessage(parsed);
      if (extracted) message = extracted;
    } else if (looksLikeHtml(rawText)) {
      message = buildNonJsonMessage(url);
    }
    throw new ApiHttpError(message, { status: response.status, detail: parsed });
  }

  if (parsed === null) {
    throw new ApiHttpError(buildNonJsonMessage(url));
  }

  return parsed as T;
}

// 권한 오류 등 자주 다루는 분기를 위한 헬퍼
export function isForbidden(err: unknown): boolean {
  return err instanceof ApiHttpError && err.status === 403;
}

export function isNotFound(err: unknown): boolean {
  return err instanceof ApiHttpError && err.status === 404;
}

export function getErrorMessage(err: unknown): string {
  if (err instanceof ApiHttpError) return err.message;
  if (err instanceof Error) return err.message;
  return '알 수 없는 오류가 발생했어요';
}
